// ─────────────────────────────────────────────
//  ProviderBand — ONE person's line inside a case bar that holds two of them.
//
//  Name, their own departure, and their own diagonal shift hatch running from
//  the start of the case to the minute THEY leave.
//
//  ⚠️ THE HATCH IS THE POINT. The owner's report on the first two-provider
//  build was *"we are missing the diagonal lines that show the leaving time for
//  each CRNA."* This board already reads diagonals as "this person has gone" —
//  it is the established vocabulary (`caseBarStyles.barHatch`), and per-line
//  hatching says when each provider goes without needing a second box or a
//  second visual idea. Same diagonals, same faint-then-dark split, same 2px
//  rule at the departure edge; only the vertical extent changes, from the top
//  half of the bar to this band.
//
//  ⚠️ NO DEPARTURE MEANS NO HATCH, AND THAT IS THE HONEST ANSWER. An unhatched
//  line reads as "still here". A line hatched end to end would say the exact
//  opposite about somebody the day simply has no leaving time for — which is
//  most second CRNAs on today's board, because the sheet cell names them and
//  prints no time. `domain/caseLanes.secondCoverage` is what decides.
//
//  ⚠️ THE TWO BANDS ARE EQUALS. Same height, same type, same weight, no indent
//  and no rank mark: *"i dont care who is orienting who, place the 2 crnas."*
//  This component is used for BOTH of them and takes no "is this the seat"
//  flag, which is the cheapest way to keep it true.
// ─────────────────────────────────────────────
import React from "react";
import BarName from "./BarName";
import S from "./caseBarStyles";

export default function ProviderBand({
  // { top, height } in px inside the bar's content box — `caseLanes.barBands`.
  band,
  // Percent of the BAR at which this person's hatch ends. 0 = draw none.
  hatchPercent = 0,
  // What the width allowed (`domain/caseBarLabel.fitBarName`).
  name,
  depart = "",
  // Shift colour for the name, exactly as the single-provider bar colours it.
  color,
  // null → a plain span; otherwise the quick-actions trigger. See `BarName`.
  menu = null,
}) {
  // A single line centred in a band, WITHOUT making the band's text a flex
  // container — `text-overflow: ellipsis` needs a block container, and a name
  // too long for its bar must end in an ellipsis rather than a hard cut.
  const lineHeight = `${band.height}px`;

  return (
    <div style={{ ...S.providerBand, top: band.top, height: band.height }}>
      {hatchPercent > 0 && (
        <span aria-hidden="true" style={{ ...S.bandHatch, width: `${hatchPercent}%` }}>
          {/* ⚠️ THE CLONE MUST BE THE DEGRADED TEXT. It exists to size the faint
              zone to where the real label ends — the divider then lands exactly
              at the end of the name with no runtime measuring — so cloning the
              full name onto a bar that is printing "Klaphake" would put it past
              the text it is supposed to end at. */}
          <span style={{ ...S.barHatchFaint, lineHeight }}>
            {name}
            {depart ? <span style={S.barDepart}> {depart}</span> : null}
          </span>
          <span style={S.barHatchDark} />
        </span>
      )}
      <BarName
        text={name}
        depart={depart}
        style={{ ...S.providerLine, lineHeight, color }}
        menu={menu}
      />
    </div>
  );
}
